import * as THREE from 'three';
import type { ImportedModelInstancedUnitSource } from '../../types';

/**
 * 相机使用的真实模型空间查询。每个实例槽独立提供范围，派生边线/高亮/拓扑宿主不参与。
 * 只借用正式几何和已提交矩阵，不恢复 source、不创建渲染资源；不依赖 UI 或具体相机模式。
 */
export class CameraModelSpatialQuery implements CameraModelSpatialQueryPort, CameraClippingBoundsSourcePort {
    /** 正式批次映射，包含装配与独立阵列。 */
    private readonly entries = new Map<THREE.Object3D, CameraModelInstanceEntry>();
    /** 反向查询逻辑父级，兼容多层 source 一并脱离的情况。 */
    private readonly sourceEntries = new Map<THREE.Object3D, CameraModelInstanceEntry>();
    /** 复用查询缓冲，回调只允许同步读取。 */
    private readonly bounds = new THREE.Box3();
    /** 世界变换与槽变换分开保存，不能覆写正式矩阵。 */
    private readonly worldMatrix = new THREE.Matrix4();
    /** 单个实例的局部 GPU 矩阵。 */
    private readonly instanceMatrix = new THREE.Matrix4();
    /** 深度优先遍历的复用栈，支持剪掉整个派生子树。 */
    private readonly stack: THREE.Object3D[] = [];
    /** Viewer 生命周期成员订阅。 */
    private unsubscribe: (() => void) | null = null;
    /** 正式实例成员或共享空间版本变化才推进，不跟随相机。 */
    private clippingRevision = 0;
    /** 成员变化不能与单槽更新混用，撤销 Unit 后必须重建业务来源映射。 */
    private clippingMembershipRevision = 0;

    /** @param units 可选 Unit 成员端口；普通独立 SDK 场景也能使用本查询。 */
    public constructor(units?: ImportedModelInstancedUnitSource) {
        try { this.unsubscribe = units?.observeUnits((unit, present) => {
            this.removeUnit(unit.object);
            if (!present) return;
            const entry: CameraModelInstanceEntry = {
                unit, sources: unit.listSourceMeshes(),
                unsubscribe: unit.onDispose(() => this.removeUnit(unit.object)),
            };
            this.entries.set(unit.object, entry);
            for (const source of entry.sources) this.sourceEntries.set(source, entry);
            this.clippingRevision++;
            this.clippingMembershipRevision++;
        }) ?? null; }
        catch (error) { this.dispose(); throw error; }
    }

    /**
     * 遍历真实模型自身范围，不递归合并隐藏子对象。
     * @param scene 查询范围。
     * @param shouldParticipate 相机已有基准/草图等资格规则。
     * @param visit 单个显示源的同步读取回调。
     */
    public forEachModel(scene: THREE.Object3D, shouldParticipate: (object: THREE.Object3D) => boolean, visit: CameraModelBoundsVisitor,
        reuseClippingBounds = false): void {
        this.stack.push(scene);
        try {
            while (this.stack.length) {
                const object = this.stack.pop()!;
                if (!object.visible || this.isDerived(object)) continue;
                const entry = this.entries.get(object);
                if (entry) {
                    if (!shouldParticipate(object)) continue;
                    object.updateWorldMatrix(true, false);
                    if (reuseClippingBounds) entry.unit.getSpatialBoundsRevision();
                    for (const source of entry.sources) {
                        if (!this.isSourceVisible(source, entry) || !shouldParticipate(source)) continue;
                        if (!entry.unit.copyInstanceMatrixForSourceMesh(source, this.instanceMatrix)) continue;
                        this.worldMatrix.multiplyMatrices(object.matrixWorld, this.instanceMatrix);
                        if (reuseClippingBounds && entry.unit.copySourceMeshLocalBounds(source, this.bounds)) {
                            // 已有局部盒可能略宽，只供安全裁剪；旋转中心仍走原精确世界范围，不能改变评分。
                            this.bounds.applyMatrix4(object.matrixWorld);
                            const { min, max } = this.bounds;
                            if (!this.bounds.isEmpty() && Number.isFinite(min.x) && Number.isFinite(min.y) && Number.isFinite(min.z)
                                && Number.isFinite(max.x) && Number.isFinite(max.y) && Number.isFinite(max.z)) {
                                visit(source, this.bounds, this.worldMatrix, true);
                            }
                            continue;
                        }
                        if (this.readGeometryBounds(entry.unit.object.geometry, this.worldMatrix)) visit(source, this.bounds, this.worldMatrix, true);
                    }
                    // 正式 Unit 的边线和临时投影已由其 source 表达，禁止作为第二份模型范围。
                    continue;
                }
                if (!this.sourceEntries.has(object) && shouldParticipate(object)) {
                    object.updateWorldMatrix(true, false);
                    this.worldMatrix.copy(object.matrixWorld);
                    if (object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points) {
                        if (object instanceof THREE.InstancedMesh) {
                            // 非 imported SDK 宿主创建的原生 InstancedMesh 也必须逐槽统计。
                            for (let index = 0; index < object.count; index++) {
                                object.getMatrixAt(index, this.instanceMatrix);
                                this.worldMatrix.multiplyMatrices(object.matrixWorld, this.instanceMatrix);
                                if (this.readGeometryBounds(object.geometry, this.worldMatrix)) visit(object, this.bounds, this.worldMatrix, true);
                            }
                        } else if (object instanceof THREE.SkinnedMesh) {
                            // 保留普通骨骼 Mesh 的对象级包围盒语义，不退回未变形的模板几何。
                            if (!object.boundingBox) object.computeBoundingBox();
                            if (object.boundingBox) {
                                this.bounds.copy(object.boundingBox).applyMatrix4(this.worldMatrix);
                                if (!this.bounds.isEmpty()) visit(object, this.bounds, this.worldMatrix, false);
                            }
                        } else if (this.readGeometryBounds(object.geometry, this.worldMatrix)) visit(object, this.bounds, this.worldMatrix, false);
                    } else if (object instanceof THREE.Sprite) {
                        this.bounds.setFromObject(object);
                        if (!this.bounds.isEmpty()) visit(object, this.bounds, this.worldMatrix, false);
                    }
                }
                for (let index = object.children.length - 1; index >= 0; index--) this.stack.push(object.children[index]);
            }
        } finally { this.stack.length = 0; }
    }

    /** @returns 正式实例范围修订；索引只校验缓冲/几何版本，不刷新未变化槽位。 */
    public getClippingRevision(): number {
        for (const entry of this.entries.values()) {
            const revision = entry.unit.getSpatialBoundsRevision();
            if (entry.boundsRevision !== revision) {
                entry.boundsRevision = revision;
                this.clippingRevision++;
                entry.boundsChangedAt = this.clippingRevision;
            }
        }
        return this.clippingRevision;
    }

    /** @returns 实例成员结构版本。 */
    public getClippingMembershipRevision(): number { return this.clippingMembershipRevision; }

    /** @param since 上次已读取修订。 @param visit 当前变化的正式实例组。 */
    public forEachChangedClippingOwner(since: number, visit: (owner: THREE.Object3D) => void): void {
        for (const entry of this.entries.values()) {
            if ((entry.boundsChangedAt ?? -1) > since) visit(entry.unit.object);
        }
    }

    /** @param source 业务显示来源。 @returns 范围拥有者，不按名称猜测实例归属。 */
    public resolveClippingOwner(source: THREE.Object3D): THREE.Object3D {
        return this.sourceEntries.get(source)?.unit.object ?? source;
    }

    /**
     * 枚举一次裁剪缓存依赖；隐藏层级也保留直接属性快照，以便再次显示时立即失效。
     * @param scene 物理场景。
     * @param visit 由消费者去重并保存依赖。
     */
    public forEachClippingDependency(scene: THREE.Object3D, visit: (object: THREE.Object3D) => void): void {
        this.stack.push(scene);
        try {
            while (this.stack.length) {
                const object = this.stack.pop()!;
                if (this.isDerived(object)) { visit(object); continue; }
                // 不把独立灯光或相机变换当成模型变化；模型真实祖先依然完整登记。
                if (object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points || object instanceof THREE.Sprite) {
                    let ancestor: THREE.Object3D | null = object;
                    while (ancestor) { visit(ancestor); ancestor = ancestor.parent; }
                }
                const entry = this.entries.get(object);
                if (entry) {
                    for (const source of entry.sources) {
                        visit(source);
                        let parent = entry.unit.resolveSourceMeshOriginalParent(source) ?? entry.unit.resolveOccurrenceRoot(source);
                        while (parent && parent !== source) {
                            visit(parent);
                            parent = parent.parent ?? this.sourceEntries.get(parent)?.unit.resolveSourceMeshOriginalParent(parent as THREE.Mesh) ?? null;
                        }
                    }
                    continue;
                }
                for (let index = object.children.length - 1; index >= 0; index--) this.stack.push(object.children[index]);
            }
        } finally { this.stack.length = 0; }
    }

    /** @param scene 场景。 @param predicate 既有资格规则。 @param visit 裁剪范围访问器。
     * @param predicate
     * @param visit
     */
    public forEachClippingModel(scene: THREE.Object3D, predicate: (object: THREE.Object3D) => boolean, visit: CameraModelBoundsVisitor): void {
        // 增量查询可以从单个 Mesh/Unit 开始，但不能跳过物理祖先的隐藏/派生资格。
        let parent = scene.parent;
        while (parent) {
            if (!parent.visible || this.isDerived(parent)) return;
            parent = parent.parent;
        }
        this.forEachModel(scene, predicate, visit, true);
    }

    /** 解除成员与 Unit 销毁订阅，不销毁任何模型资产。 */
    public dispose(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        for (const entry of this.entries.values()) entry.unsubscribe();
        this.entries.clear();
        this.sourceEntries.clear();
        this.stack.length = 0;
        this.clippingRevision++;
        this.clippingMembershipRevision++;
    }

    /** @param object 待撤销发布的正式对象。 */
    private removeUnit(object: THREE.Object3D): void {
        const entry = this.entries.get(object);
        if (!entry) return;
        entry.unsubscribe();
        for (const source of entry.sources) this.sourceEntries.delete(source);
        this.entries.delete(object);
        this.clippingRevision++;
        this.clippingMembershipRevision++;
    }

    /** @param geometry 正式几何。 @param matrix 单实例世界矩阵。 @returns 是否有有限范围。 */
    private readGeometryBounds(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): boolean {
        if (!geometry.boundingBox) geometry.computeBoundingBox();
        if (!geometry.boundingBox) return false;
        this.bounds.copy(geometry.boundingBox).applyMatrix4(matrix);
        const { min, max } = this.bounds;
        return !this.bounds.isEmpty() && Number.isFinite(min.x) && Number.isFinite(min.y) && Number.isFinite(min.z)
            && Number.isFinite(max.x) && Number.isFinite(max.y) && Number.isFinite(max.z);
    }

    /** @param source 正式业务源。 @param entry 活动 Unit。 @returns 逻辑父链是否可见。 */
    private isSourceVisible(source: THREE.Mesh, entry: CameraModelInstanceEntry): boolean {
        if (!source.visible) return false;
        let parent = entry.unit.resolveSourceMeshOriginalParent(source) ?? entry.unit.resolveOccurrenceRoot(source);
        while (parent && parent !== source) {
            if (!parent.visible || this.isDerived(parent)) return false;
            parent = parent.parent ?? this.sourceEntries.get(parent)?.unit.resolveSourceMeshOriginalParent(parent as THREE.Mesh) ?? null;
        }
        return true;
    }

    /** @param object 物理节点。 @returns 是否是非业务模型的派生显示子树。 */
    public isDerived(object: THREE.Object3D): boolean {
        return Boolean(object.userData.isImportedModelOverlay || object.userData.skipSelectionSubtree
            || Reflect.get(object, 'ignore') || Reflect.get(object, 'isFeatureFace')
            || Reflect.get(object, 'isFeatureLine') || Reflect.get(object, 'isFeaturePoint')
            || object.name.startsWith('imported-topology-host:'));
    }
}
